import { UwContext } from '@ps20-frontend/uw';
export interface LegacyUwUploadedFile {
  fileName: string;
  uploadId: string;
  uploadedFileSize?: number;
}

export interface LegacyUwMeasure {
  type: string;
  zuschlagProzent?: number;
  vereinbarungText?: string;
}

export interface LegacyUwDecision {
  vertragZeichnen: boolean | null;
  massnahmen: LegacyUwMeasure[];
  dokumentation: string;
  grund: string;
  risikoAddress?: string;
  produktName?: string;
  timestamp?: string;
  isAcceptedByAgent?: boolean;
}

export interface LegacyUwQuestionnaire {
  files: LegacyUwUploadedFile[];
  beschreibung?: string;
  comment?: string;
  uwDecision?: LegacyUwDecision;
  underwriterName?: string;
  underwriterBensl?: string;
}

export type MigratedUwContextKey = UwContext.NATURAL_HAZARDS | UwContext.OUTBUILDING;

export interface MigratedUwContextEntry {
  contextKey: MigratedUwContextKey;
  isRequired: boolean;
  willUnderwrite: boolean | null;
  isAcceptedByAgent?: boolean;
  files: LegacyUwUploadedFile[];
  texts: Record<string, string>;
  reason: string;
}

export interface MigratedUwSharedDecision {
  measures: LegacyUwMeasure[];
  documentation: string;
  underwriterName: string;
  underwriterBensl: string;
  risikoAddress: string;
  produktName: string;
  timestamp?: string;
}

export interface MigratedProductUw {
  shared: MigratedUwSharedDecision;
  entries: MigratedUwContextEntry[];
}

export interface FoldUwContextsInput {
  uw?: MigratedProductUw;
  naturalHazards?: LegacyUwQuestionnaire;
  outbuilding?: LegacyUwQuestionnaire;
  isUnderwriterInfoVisible?: boolean;
  showElementarfragebogen?: boolean;
  showNebengebaeude?: boolean;
}

function hasMeaningfulContent(
  questionnaire: LegacyUwQuestionnaire | undefined,
): questionnaire is LegacyUwQuestionnaire {
  if (!questionnaire) {
    return false;
  }
  return (
    questionnaire.files.length > 0 ||
    Boolean(questionnaire.beschreibung) ||
    Boolean(questionnaire.comment) ||
    questionnaire.uwDecision !== undefined
  );
}

function buildTexts(questionnaire: LegacyUwQuestionnaire): Record<string, string> {
  const texts: Record<string, string> = {};
  if (questionnaire.beschreibung) {
    texts.beschreibung = questionnaire.beschreibung;
  }
  if (questionnaire.comment) {
    texts.comment = questionnaire.comment;
  }
  return texts;
}

function resolveIsRequired(
  isUnderwriterInfoVisible: boolean | undefined,
  flag: boolean | undefined,
): boolean {
  if (isUnderwriterInfoVisible === false) {
    return false;
  }
  return flag === true;
}

function buildEntry(
  contextKey: MigratedUwContextKey,
  questionnaire: LegacyUwQuestionnaire | undefined,
  isRequired: boolean,
): MigratedUwContextEntry | undefined {
  if (!isRequired && !hasMeaningfulContent(questionnaire)) {
    return undefined;
  }
  return {
    contextKey,
    isRequired,
    willUnderwrite: questionnaire?.uwDecision?.vertragZeichnen ?? null,
    isAcceptedByAgent: questionnaire?.uwDecision?.isAcceptedByAgent,
    files: questionnaire?.files ?? [],
    texts: questionnaire ? buildTexts(questionnaire) : {},
    reason: questionnaire?.uwDecision?.grund ?? '',
  };
}

function buildSharedDecision(
  naturalHazards: LegacyUwQuestionnaire | undefined,
  outbuilding: LegacyUwQuestionnaire | undefined,
): MigratedUwSharedDecision {
  const outbuildingSource = outbuilding?.uwDecision ? outbuilding : undefined;
  const decisionSource = naturalHazards?.uwDecision ? naturalHazards : outbuildingSource;

  return {
    measures: decisionSource?.uwDecision?.massnahmen ?? [],
    documentation: decisionSource?.uwDecision?.dokumentation ?? '',
    underwriterName: decisionSource?.underwriterName ?? '',
    underwriterBensl: decisionSource?.underwriterBensl ?? '',
    risikoAddress: decisionSource?.uwDecision?.risikoAddress ?? '',
    produktName: decisionSource?.uwDecision?.produktName ?? '',
    timestamp: decisionSource?.uwDecision?.timestamp,
  };
}

export function foldUwContexts(input: FoldUwContextsInput): MigratedProductUw | undefined {
  if (input.uw) {
    return input.uw;
  }

  const isLegacyElementarOnly =
    input.showElementarfragebogen === undefined && input.showNebengebaeude === undefined;

  const naturalHazardsRequired = isLegacyElementarOnly
    ? input.isUnderwriterInfoVisible === true
    : resolveIsRequired(input.isUnderwriterInfoVisible, input.showElementarfragebogen);
  const outbuildingRequired = resolveIsRequired(
    input.isUnderwriterInfoVisible,
    input.showNebengebaeude,
  );

  const naturalHazardsEntry = buildEntry(
    UwContext.NATURAL_HAZARDS,
    input.naturalHazards,
    naturalHazardsRequired,
  );
  const outbuildingEntry = isLegacyElementarOnly
    ? undefined
    : buildEntry(UwContext.OUTBUILDING, input.outbuilding, outbuildingRequired);

  const entries = [naturalHazardsEntry, outbuildingEntry].filter(
    (entry): entry is MigratedUwContextEntry => entry !== undefined,
  );

  if (entries.length === 0) {
    return undefined;
  }

  return {
    shared: buildSharedDecision(input.naturalHazards, input.outbuilding),
    entries,
  };
}

export interface ProductUwWithOptionalRequired extends Omit<MigratedProductUw, 'entries'> {
  entries: Array<Omit<MigratedUwContextEntry, 'isRequired'> & { isRequired?: boolean }>;
}

export function backfillUwEntriesRequired(
  uw: ProductUwWithOptionalRequired | undefined,
): MigratedProductUw | undefined {
  if (!uw) {
    return undefined;
  }
  return {
    ...uw,
    entries: (uw.entries ?? []).map(entry => ({
      ...entry,
      isRequired: entry.isRequired ?? true,
    })),
  };
}
